import { ContractData } from "../../data/contractData";
import { SMSPacket } from "../../data/smsPacket";
import { ContractModel } from "../../models/contracts/contractModel";
import { ContractsModel } from "../../models/contracts/contractsModel";
import { CardsStates } from "../../data/enums/cardsStates";
import IContractService from "../../services/contracts/IContractService";
import { DiTokens } from "../../services/diTokens";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { container } from "@/inversify.config";
import { RequestParamsModel } from "../../models/statement/requestParamsModel";
import { IActionModel } from "../../models/IActionModel";
import { Dictionary } from "../../data/types/index";
import { AccountDetailData } from "../../data/accountDetailData";

@Module({
  name: "contracts",
  namespaced: true
})
export class ContractsStore extends VuexModule {
  get contractService(): IContractService {
    return container.get<IContractService>(DiTokens.ContractService);
  }

  get cardService(): IContractService {
    return container.get<IContractService>(DiTokens.CardService);
  }

  /** Сгруппированные счета */
  public contracts: ContractsModel = new ContractsModel();

  /**Признак загрузки */
  public loading: boolean = true;

  /**Методы для вызова после заполнения счетов */
  public callAfterAcntFilling: Function[] = [];

  public smsPackets: Array<SMSPacket> = [];

  public forecastLoading: boolean = true;

  public static getDefaultState: Function = () => {
    return {
      contracts: new ContractModel(),
      loading: true,
      callAfterAcntFilling: [],
      smsPackets: [],
      forecastLoading: true      
    }
  };

  public selectedCardOrContract: string | null = null;

  @Mutation
  setSelected(item: string) {
    this.selectedCardOrContract = item;
  }

  /**Установка признака загрузки */
  @Mutation
  setLoading(loading: boolean) {
    this.loading = loading;
  }

  @Mutation
  resetStates() {
    Object.assign(this, ContractsStore.getDefaultState());
  }

  /**Обновить все счета */
  @Mutation
  setContracts(contracts: ContractsModel) {
    this.contracts = contracts;
  }

  /**Обновить реквизиты счета */
  @Mutation
  setBankDetails(data: Dictionary<any>) {
    const contract = this.contracts.getContract(data.contractId as number);
    contract.BankDetails = data.bankDetails as AccountDetailData;
    this.contracts.updateContract(contract.Data);
  }

  /**Обновить данные счета */
  @Mutation
  setContract(updatedContract: ContractData) {
    this.contracts.updateContract(updatedContract);
  }

  /**Обновить алиас счета */
  @Mutation
  updateContractAlias(data: Dictionary<any>) {
    const contract = this.contracts.getContract(data.contractId as number);
    contract.Data.Alias = data.alias as string;
    this.contracts.updateContract(contract.Data);
  }

  @Mutation
  setSmsPacket(arr: Array<SMSPacket>) {
    this.smsPackets = arr;
  }

  /**Обновить алиас карты */
  @Mutation
  updateCardAlias(data: Dictionary<any>) {
    const card = this.contracts.getCard(
      data.contractId as number,
      data.cardId as number
    );
    card.Data.Alias = data.alias as string;
    this.contracts.updateCard(card.Data, data.contractId as number);
  }

  /**Обновить видимость счета */
  @Mutation
  updateContractVisibility(data: Dictionary<any>) {
    const contract = this.contracts.getContract(data.contractId as number);
    contract.Data.Hidden = !(data.hidden as boolean);
    this.contracts.updateContract(contract.Data);
  }

  /**Обновить видимость карты */
  @Mutation
  updateCardVisibility(data: Dictionary<any>) {
    const card = this.contracts.getCard(
      data.contractId as number,
      data.cardId as number
    );
    card.Data.Hidden = data.hidden as boolean;
    this.contracts.updateCard(card.Data, data.contractId);
  }

  @Mutation
  updateCardBlock(data: Dictionary<any>) {
    const card = this.contracts.getCard(
      data.contractId as number,
      data.cardId as number
    );
    card.Data.CardState = data.block ? CardsStates.Blocked : CardsStates.Active;
    this.contracts.updateCard(card.Data, data.contractId);
  }

  /**
   * Добавить метод для вызова после загрузки счетов
   * @param func
   */
  @Mutation
  addMethod(func: Function) {
    this.callAfterAcntFilling.push(func);
  }

  @Mutation
  setSelectedSMSPacket(code: any) {
    if (this.smsPackets.length > 0)
      this.smsPackets.map((item: any) =>
        item.Code === code ? (item.selected = true) : (item.selected = false)
      );
  }

  /**
   * Очистить список методов для вызова после загрузки счетов
   * @param func
   */
  @Mutation
  clearMethods() {
    this.callAfterAcntFilling = [];
  }

  /** 
    @param data.id идентификатор контракта
    @param data.array массив с сервера
  */
  @Mutation
  setDepositForeCast(data: Dictionary<any>) {
    const contract = this.contracts.getContract(data.id as number);
    contract.DepositForeCast = data.array;
  }

  /** 
    @param data.id идентификатор контракта
    @param data.array массив с сервера
  */
  @Mutation
  setCreditSchedule(data: Dictionary<any>) {
    const contract = this.contracts.getContract(data.id as number);
    contract.CreditSchedule = data.model;
  }

  @Mutation
  setForecastLoading(loading: boolean) {
    this.forecastLoading = loading;
  }

  /**Загрузка полного списка счетов */
  @Action
  async fetchContracts() {
    this.setLoading(true);
    const resp: ContractsModel = (await this.contractService.getList(
      []
    )) as ContractsModel;
    this.setContracts(resp);
    this.setLoading(false);
    try {
      for (const i in this.callAfterAcntFilling) {
        await this.callAfterAcntFilling[i]();
      }
      this.clearMethods();
    } catch (e) {
      this.clearMethods();
    }
  }

  /**
   * Обновление данных счета
   */
  @Action
  async updateContract(id: number) {
    const resp: ContractModel = (await this.contractService.get(
      id
    )) as ContractModel;
    this.setContract(resp.Data);
  }

  /**
   * Обновление данных счета по RefId счета
   */
  @Action
  async updateContractByRefId(refId: string) {
    const contract = this.contracts.getContractByRefId(refId);
    if (contract) {
      const resp: ContractModel = (await this.contractService.get(
        contract.Data.Id
      )) as ContractModel;
      this.setContract(resp.Data);
    }
  }

  /**
   * Обновление реквизитов счета
   */
  @Action
  async updateBankDetails(id: number): Promise<AccountDetailData> {
    const resp: AccountDetailData = (await this.contractService.getBankDetails(
      id
    )) as AccountDetailData;
    const data: Dictionary<any> = {
      contractId: id,
      bankDetails: resp
    };
    this.setBankDetails(data);
    return resp;
  }

  /**
   * Обновление псевдонима счета
   * @param alias Новый псевдоним
   */
  @Action
  async editContractAlias(actionModel: IActionModel): Promise<IActionModel> {
    actionModel = await this.contractService.editAlias(actionModel);
    if (actionModel.IsSuccess) this.updateContractAlias(actionModel.Data);
    return actionModel;
  }

  /**
   * Обновление псевдонима карты
   * @param alias Новый псевдоним
   */
  @Action
  async editCardAlias(actionModel: IActionModel): Promise<IActionModel> {
    actionModel = await this.cardService.editAlias(actionModel);
    if (actionModel.IsSuccess) this.updateCardAlias(actionModel.Data);
    return actionModel;
  }

  /**
   * Установка видимости счета
   * @param hidden Признак скрытия счета
   */
  @Action
  async setContractVisibility({ actionModel, respCode }): Promise<IActionModel> {
    actionModel = await this.contractService.setVisibility(actionModel, respCode);
    if (actionModel.IsSuccess) this.updateContractVisibility(actionModel.Data);
    return actionModel;
  }
  /**
   * Установка видимости карты
   * @param hidden Признак скрытия счета
   */
  @Action
  async setCardVisibility({ model, code }): Promise<IActionModel> {
    model = await this.cardService.setVisibility(model, code);
    if (model.IsSuccess) this.updateCardVisibility(model.Data);
    return model;
  }

  /**Отправка реквизитов счета */
  @Action
  async sendAccountDetail(actionModel: IActionModel): Promise<IActionModel> {
    actionModel = await this.contractService.sendAccountDetail(actionModel);
    return actionModel;
  }

  @Action
  async fetchSmsPackets(id: number) {
    this.setSmsPacket(await this.contractService.getSmsPackets());
    const contract = this.contracts.getContract(id);
    if (contract) {
      this.setSelectedSMSPacket(contract.Data.SmsPacketId);
    }
  }

  /** 
    Получние списка доходности
    @param id идентификатор контракта
  */
  @Action
  async fetchForeCastDeposit(id: number) {
    this.setForecastLoading(true);
    this.setDepositForeCast({
      id: id,
      array: await this.contractService.getDepositForeCast(id)
    });
    this.setForecastLoading(false);
  }

  /** 
    Получние графика кредита
    @param id идентификатор контракта
  */
  @Action
  async fetchScheduleCredit(id: number) {
    this.setForecastLoading(true);
    this.setCreditSchedule({
      id: id,
      model: await this.contractService.getCreditSchedule(
        new RequestParamsModel({
          dateBegin: "",
          dateEnd: "",
          contractId: id
        })
      )
    });
    this.setForecastLoading(false);
  }
}
